import styled from 'styled-components';
import {
  PRIMARY_TEXT_COLOR,
  SECONDARY_TEXT_COLOR,
  BORDER_COLOR,
  BACKGROUND_COLOR,
  PRIMARY_COLOR,
  HOVER_COLOR,
  SECONDARY_COLOR,
  LIGHT_BG_FOR_CARDS,
  NICE_BG_FOR_CARDS,
} from '../../../constants';
import { desktopMediaQuery } from '../../../styleHelpers';

export const CharacterCreationContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  padding-bottom: 160px;
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  color: ${PRIMARY_TEXT_COLOR};
  margin-bottom: 16px;
  text-align: center;
`;

export const PageDescription = styled.p`
  font-size: 16px;
  color: ${SECONDARY_TEXT_COLOR};
  max-width: 800px;
  margin: 0 auto 32px;
`;

export const AttributesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const AttributeGroupContainer = styled.section`
  border: 1px solid ${BORDER_COLOR};
  border-radius: 8px;
  padding: 12px;
  padding-top: 4px;
  background-color: ${BACKGROUND_COLOR};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const GroupTitle = styled.h2`
  font-size: 24px;
  color: ${PRIMARY_TEXT_COLOR};
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${BORDER_COLOR};
`;

export const AttributeSectionContainer = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const AttributeTitle = styled.h3`
  font-size: 16px;
  color: ${PRIMARY_TEXT_COLOR};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AttributeDescription = styled.p`
  font-size: 14px;
  color: ${SECONDARY_TEXT_COLOR};
  margin-bottom: 16px;
`;

export const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
`;

export const OptionButton = styled.button<{ isSelected: boolean }>`
  padding: 6px 8px;
  min-width: 70px;
  border-radius: 20px;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? PRIMARY_COLOR : BORDER_COLOR)};
  background-color: ${({ isSelected }) =>
    isSelected ? PRIMARY_COLOR : 'transparent'};
  color: ${({ isSelected }) =>
    isSelected ? PRIMARY_TEXT_COLOR : PRIMARY_TEXT_COLOR};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? PRIMARY_COLOR : NICE_BG_FOR_CARDS};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ShowMoreButton = styled(OptionButton).attrs({ isSelected: false })`
  border: 1px dashed ${BORDER_COLOR};
`;

export const RandomizeAttributeButton = styled.button`
  background: none;
  border: none;
  color: ${PRIMARY_COLOR};
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  padding: 4px;
  margin-left: auto;
  &:hover {
    color: ${SECONDARY_COLOR};
    text-decoration: underline;
  }
`;

export const RandomizeAllContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 32px 0;
`;

export const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-top: 32px;
  border-top: 1px solid ${BORDER_COLOR};
  background-color: ${BACKGROUND_COLOR};
  z-index: 10;

  padding-top: 8px;
  padding-bottom: 8px;

  position: fixed;
  bottom: 58px;
  ${desktopMediaQuery} {
    bottom: 0;
  }
  left: 0;
  right: 0;
`;

export const NavButton = styled.button<{ isPrimary?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 8px;

  background-color: ${({ isPrimary }) =>
    isPrimary ? PRIMARY_COLOR : 'transparent'};
  color: ${({ isPrimary }) => (isPrimary ? PRIMARY_TEXT_COLOR : 'white')};
  border: ${({ isPrimary }) => (isPrimary ? 'none' : `1px solid white`)};
  cursor: pointer;

  font-weight: bold;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${({ isPrimary }) =>
      isPrimary ? PRIMARY_COLOR : 'transparent'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CharacterSummaryContainer = styled.div`
  background-color: ${LIGHT_BG_FOR_CARDS};
  border-radius: 8px;
  padding: 12px 24px;
  margin-top: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const SummaryTitle = styled.h3`
  font-size: 18px;
  color: ${PRIMARY_TEXT_COLOR};
  margin-bottom: 16px;
  text-align: center;
`;

export const SummaryItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  font-size: 14px;
  ${desktopMediaQuery} {
    max-height: 250px;
    flex-wrap: wrap;
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  margin-left: 16px;
  margin-bottom: 8px;
  font-size: 14px;
`;

export const SummaryLabel = styled.span`
  font-weight: bold;
  color: ${PRIMARY_TEXT_COLOR};
  width: 120px;
  flex-shrink: 0;
`;

export const SummaryValue = styled.span`
  color: ${SECONDARY_TEXT_COLOR};
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 2rem;
`;

export const ActionButton = styled.button<{
  variant: 'primary' | 'secondary' | 'outline';
}>`
  padding: 12px 24px;
  border-radius: 8px;
  background-color: ${({ variant }) =>
    variant === 'primary'
      ? PRIMARY_COLOR
      : variant === 'secondary'
      ? SECONDARY_COLOR
      : 'transparent'};
  color: ${({ variant }) =>
    variant === 'primary'
      ? PRIMARY_TEXT_COLOR
      : variant === 'secondary'
      ? SECONDARY_TEXT_COLOR
      : PRIMARY_TEXT_COLOR};
  border: ${({ variant }) =>
    variant === 'outline' ? `1px solid ${PRIMARY_COLOR}` : 'none'};
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  ${desktopMediaQuery} {
    &:hover {
      background-color: ${({ variant }) =>
        variant === 'primary'
          ? PRIMARY_COLOR
          : variant === 'secondary'
          ? SECONDARY_COLOR
          : HOVER_COLOR};
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

export const ProgressContainer = styled.div`
  margin: 16px 0 32px;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${LIGHT_BG_FOR_CARDS};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

export const ProgressIndicator = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => `${progress}%`};
  background-color: ${PRIMARY_COLOR};
  transition: width 0.3s ease;
`;

export const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14.4px;
  color: ${SECONDARY_TEXT_COLOR};
`;

export const ErrorText = styled.div`
  color: 'red';
  font-size: 14.4px;
  margin-top: 8px;
`;

export const CustomInputContainer = styled.div`
  margin-top: 12px;
  width: 80%;
`;

export const CharacterImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0;
`;

export const CharacterImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;
